// src/theme/m3.js
// Tokens Material 3 (Material You) derivados del theme de la app.
// M3 no viene en MUI, así que reconstruimos los "color roles" que dan
// el look de Android: contenedores tonales, superficies con tinte de
// color según elevación, formas muy redondeadas y botones tipo píldora.
//
// Vive aparte porque lo comparten varias pantallas (Mi cuenta,
// Configuración…): si cada una lo redefine, el mismo "contenedor
// secundario" termina con dos tonos distintos y la app se ve cosida.
import { useMemo } from "react";
import { alpha, darken, lighten, useTheme } from "@mui/material/styles";

export const SHAPE = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 28,
  full: 999,
};

/* --- Tipografía M3 (roles usados en la app) ----------------------- */
export const type = {
  displaySmall: { fontSize: 36, lineHeight: 1.22, fontWeight: 400, letterSpacing: 0 },
  headlineSmall: { fontSize: 24, lineHeight: 1.33, fontWeight: 400, letterSpacing: 0 },
  titleLarge: { fontSize: 22, lineHeight: 1.27, fontWeight: 400, letterSpacing: 0 },
  titleMedium: { fontSize: 16, lineHeight: 1.5, fontWeight: 500, letterSpacing: 0.15 },
  bodyMedium: { fontSize: 14, lineHeight: 1.43, fontWeight: 400, letterSpacing: 0.25 },
  bodySmall: { fontSize: 12, lineHeight: 1.33, fontWeight: 400, letterSpacing: 0.4 },
  labelLarge: { fontSize: 14, lineHeight: 1.43, fontWeight: 500, letterSpacing: 0.1 },
  labelMedium: { fontSize: 12, lineHeight: 1.33, fontWeight: 500, letterSpacing: 0.5 },
};

/**
 * Roles de color M3 calculados sobre el theme activo.
 * Al depender de `useTheme()` funciona dentro de cualquier ThemeProvider,
 * incluido el provider local que usa la vista previa de Configuración.
 */
export const useM3 = () => {
  const t = useTheme();
  return useMemo(() => {
    const dark = t.palette.mode === "dark";
    const p = t.palette.primary.main;
    const s = t.palette.secondary?.main || p;
    const e = t.palette.error.main;
    const g = t.palette.success.main;
    const paper = t.palette.background.paper;

    // M3 tinta las superficies con el color primario según la "elevación".
    // Se aplica como capa sobre el fondo real para no depender de color-mix.
    const surface = (opacity = 0) =>
      opacity === 0
        ? paper
        : `linear-gradient(0deg, ${alpha(p, opacity)}, ${alpha(p, opacity)}), ${paper}`;

    const container = (color, strong = false) =>
      dark ? darken(color, strong ? 0.5 : 0.62) : lighten(color, strong ? 0.8 : 0.88);
    const onContainer = (color) => (dark ? lighten(color, 0.78) : darken(color, 0.52));

    return {
      dark,
      primary: p,
      onPrimary: t.palette.primary.contrastText,
      primaryContainer: container(p, true),
      onPrimaryContainer: onContainer(p),
      secondaryContainer: container(s),
      onSecondaryContainer: onContainer(s),
      errorContainer: container(e),
      onErrorContainer: onContainer(e),
      error: e,
      success: g,
      onSuccess: t.palette.success.contrastText,
      successContainer: container(g),
      onSuccessContainer: onContainer(g),
      outline: dark ? alpha("#fff", 0.24) : alpha("#000", 0.18),
      onSurface: t.palette.text.primary,
      onSurfaceVariant: t.palette.text.secondary,
      surface,
      surfaceLowest: surface(0),
      surfaceLow: surface(0.04),
      surfaceContainer: surface(0.07),
      surfaceHigh: surface(0.1),
      inverseSurface: dark ? "#E6E0E9" : "#322F35",
      inverseOnSurface: dark ? "#322F35" : "#F5EFF7",
      stateLayer: (opacity = 0.08) => alpha(p, opacity),
    };
  }, [t]);
};
